import { useStytchMember } from "@stytch/react/b2b";
import { useState, useMemo, useEffect } from 'react';
import { useListStagedSessionPositions, useActionOnStagedSessionPositions, BrokerageAccountPosition, BrokerageAccountPositionStaging, BrokerageStagedPositionsDiffResult, DiffBreakdownResult, UNKNOWN_TRADER_ID } from '@/lib/api/risk/brokerage';
import { useListBaseTraders } from '@/lib/api/trader';
import { ComboboxMulti } from '@/components/ui/combobox_mutli';
import { SelectOptionItem } from '@/lib/models/ui';
import { PositionCard } from './PositionCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { Loader, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GenerateLoadingOrError } from '@/lib/utils';
import { Combobox } from '@/components/ui/combobox';

type ViewMode = 'default' | 'alternate';

const VIEW_STORAGE_KEY = 'open-orders-view-mode';

function getInitialViewMode(user_id?: string | null): ViewMode {
    if (typeof window === 'undefined') return 'default';
    const fromUrl = new URLSearchParams(window.location.search).get('view');
    if (fromUrl === 'alternate' || fromUrl === 'default') return fromUrl;
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === 'alternate' || stored === 'default') return stored;
    if (user_id === '3b968826-94b8-4f27-9c08-899cf52dedb6') {
      return 'alternate';
    }
    return 'default';
}

type SuggestionType = 'add' | 'skip' | 'unknown';

interface StagedSectionConfig {
    key: string;
    title: string;
    data: BrokerageAccountPositionStaging[];
    colorClass?: string;
    suggestionType: SuggestionType;
    defaultCollapsed?: boolean;
    /** When false, cards render as non-staged with no selection buttons (e.g. removed). */
    interactive?: boolean;
    shouldHide?: boolean;
}

function buildSections(
    staged: DiffBreakdownResult,
    viewMode: ViewMode,
): StagedSectionConfig[] {
    if (viewMode === 'alternate') {
        return [
            {
                key: 'acceptHedgeable',
                title: 'Suggested Accept Hedgeable',
                data: staged.suggested_accept_hedgeable || [],
                colorClass: 'text-green-600',
                suggestionType: 'add',
            },
            {
                key: 'acceptNonHedgeable',
                title: 'Suggested Accept Non-Hedgeable',
                data: staged.suggested_accept_non_hedgeable || [],
                colorClass: 'text-blue-600',
                suggestionType: 'add',
            },
            {
                key: 'suggestedAccept',
                title: 'Suggested Accept',
                data: staged.suggested_accept || [],
                suggestionType: 'add',
            },
            {
                key: 'dismiss',
                title: 'Suggested Dismiss',
                data: staged.suggested_dismiss || [],
                suggestionType: 'skip',
            },
            {
                key: 'nonApplicable',
                title: 'Suggested Non-Applicable',
                data: staged.suggested_non_applicable || [],
                suggestionType: 'unknown',
            },
            {
                key: 'flagged',
                title: 'Flagged',
                data: staged.suggested_flagged || [],
                suggestionType: 'unknown',
            },
            {
                key: 'removed',
                title: 'Removed since last update',
                data: staged.removed || [],
                colorClass: 'text-gray-400 font-normal',
                suggestionType: 'unknown',
                interactive: false,
            },
            {
                key: 'acceptHedgeableCarryover',
                title: 'Hedgeable Carryover',
                data: staged.suggested_accept_hedgeable_carryover || [],
                colorClass: 'text-green-700',
                suggestionType: 'add',
                defaultCollapsed: true,
                shouldHide: true,
            },
            {
                key: 'acceptNonHedgeableCarryover',
                title: 'Non-Hedgeable Carryover',
                data: staged.suggested_accept_non_hedgeable_carryover || [],
                colorClass: 'text-blue-700',
                suggestionType: 'add',
                defaultCollapsed: true,
                shouldHide: true,
            },
            {
                key: 'suggestedAcceptCarryover',
                title: 'Accept Carryover',
                data: staged.suggested_accept_carryover || [],
                suggestionType: 'add',
                defaultCollapsed: true,
                shouldHide: true,
            },
        ];
    }

    return [
        {
            key: 'acceptHedgeable',
            title: 'Suggested Accept Hedgeable',
            data: staged.suggested_accept_hedgeable || [],
            colorClass: 'text-green-600',
            suggestionType: 'add',
        },
        {
            key: 'acceptNonHedgeable',
            title: 'Suggested Accept Non-Hedgeable',
            data: staged.suggested_accept_non_hedgeable || [],
            colorClass: 'text-blue-600',
            suggestionType: 'add',
        },
        {
            key: 'acceptHedgeableCarryover',
            title: 'Hedgeable Carryover',
            data: staged.suggested_accept_hedgeable_carryover || [],
            colorClass: 'text-green-700',
            suggestionType: 'add',
        },
        {
            key: 'acceptNonHedgeableCarryover',
            title: 'Non-Hedgeable Carryover',
            data: staged.suggested_accept_non_hedgeable_carryover || [],
            colorClass: 'text-blue-700',
            suggestionType: 'add',
        },
        {
            key: 'flagged',
            title: 'Suggested Flagged',
            data: staged.suggested_flagged || [],
            suggestionType: 'unknown',
        },
        {
            key: 'dismiss',
            title: 'Suggested Dismiss',
            data: staged.suggested_dismiss || [],
            suggestionType: 'skip',
        },
        {
            key: 'nonApplicable',
            title: 'Suggested Non-Applicable',
            data: staged.suggested_non_applicable || [],
            suggestionType: 'unknown',
        },
        {
            key: 'removed',
            title: 'Removed',
            data: staged.removed || [],
            suggestionType: 'unknown',
            interactive: false,
        },
    ];
}

interface StagedSectionProps {
    section: StagedSectionConfig;
    collapsed: boolean;
    onToggle: () => void;
    sortStagingPositions: (positions: BrokerageAccountPositionStaging[]) => BrokerageAccountPositionStaging[];
    getSelectionState: (id: string) => 'hedgeable' | 'non_hedgeable' | 'dismiss' | null;
    onSelectHedgeable: (id: string) => void;
    onSelectNonHedgeable: (id: string) => void;
    onSelectDismiss: (id: string) => void;
}

function StagedSection({
    section,
    collapsed,
    onToggle,
    sortStagingPositions,
    getSelectionState,
    onSelectHedgeable,
    onSelectNonHedgeable,
    onSelectDismiss,
}: StagedSectionProps) {
    if (section.data.length === 0) return null;
    if (section.shouldHide) return null;
    const interactive = section.interactive !== false;

    return (
        <div>
            <Button
                variant="ghost"
                size="sm"
                className="w-full flex items-center justify-between"
                onClick={onToggle}
            >
                <h4 className={cn('font-semibold text-sm', section.colorClass)}>
                    {section.title} ({section.data.length})
                </h4>
                {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
            {!collapsed && (
                <div className="space-y-2">
                    {sortStagingPositions(section.data).map(staging => (
                        <PositionCard
                            key={staging.id}
                            position={staging.brokerage_account_data}
                            isStaged={interactive}
                            suggestionType={section.suggestionType}
                            selectionState={interactive ? getSelectionState(staging.id) : undefined}
                            onSelectHedgeable={interactive ? () => onSelectHedgeable(staging.id) : undefined}
                            onSelectNonHedgeable={interactive ? () => onSelectNonHedgeable(staging.id) : undefined}
                            onSelectDismiss={interactive ? () => onSelectDismiss(staging.id) : undefined}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export function OpenOrdersTab() {
    const { member } = useStytchMember();
    const { toast } = useToast();
    const [selectedTraderId, setSelectedTraderId] = useState<string | null>(null);
    const [selectedAcceptHedgeable, setSelectedAcceptHedgeable] = useState<Set<string>>(new Set());
    const [selectedAcceptNonHedgeable, setSelectedAcceptNonHedgeable] = useState<Set<string>>(new Set());
    const [selectedDismiss, setSelectedDismiss] = useState<Set<string>>(new Set());
    const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);
    const [viewMode, setViewModeState] = useState<ViewMode>(getInitialViewMode(member?.trusted_metadata.db_user_id as string | undefined));

    const diffResult = useListStagedSessionPositions();
    const tradersResult = useListBaseTraders();
    const actionOnStaged = useActionOnStagedSessionPositions();

    const traders_list = tradersResult?.data?.data;
    const diff_data: BrokerageStagedPositionsDiffResult | undefined = diffResult?.data?.success
        ? diffResult.data.data
        : undefined;

    const setViewMode = (mode: ViewMode) => {
        setViewModeState(mode);
        try {
            window.localStorage.setItem(VIEW_STORAGE_KEY, mode);
        } catch {
            // ignore persistence failures
        }
    };

    // Set default trader to current user on mount
    useEffect(() => {
        if (!selectedTraderId && traders_list?.length) {
            const currentTrader = traders_list.find((t: { is_current_user: boolean; id: string }) => t.is_current_user);
            if (currentTrader) { 
                setSelectedTraderId(currentTrader.id); 
            }
        }
    }, [traders_list, selectedTraderId]);

    // Auto-mark positions on load: suggested_accept → accepted, others → dismissed
    useEffect(() => {
        if (!diff_data?.suggested_report || !selectedTraderId) return;

        const traderData = diff_data.suggested_report[selectedTraderId];
        if (!traderData) return;

        const acceptHedgeableIds = new Set<string>();
        const acceptNonHedgeableIds = new Set<string>();
        const dismissIds = new Set<string>();

        // Hedgeable positions
        traderData.suggested_accept_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => acceptHedgeableIds.add(item.id));
        traderData.suggested_accept_hedgeable_carryover?.forEach((item: BrokerageAccountPositionStaging) => acceptHedgeableIds.add(item.id));
        // Backwards-compatible legacy accept buckets default to hedgeable
        traderData.suggested_accept?.forEach((item: BrokerageAccountPositionStaging) => acceptHedgeableIds.add(item.id));
        traderData.suggested_accept_carryover?.forEach((item: BrokerageAccountPositionStaging) => acceptHedgeableIds.add(item.id));

        // Non-hedgeable positions
        traderData.suggested_accept_non_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => acceptNonHedgeableIds.add(item.id));
        traderData.suggested_accept_non_hedgeable_carryover?.forEach((item: BrokerageAccountPositionStaging) => acceptNonHedgeableIds.add(item.id));

        // Dismissed positions
        [...(traderData.suggested_flagged || []), ...(traderData.suggested_dismiss || []), ...(traderData.suggested_non_applicable || [])]
            .forEach((item: BrokerageAccountPositionStaging) => dismissIds.add(item.id));

        // Removed are preview cards and not meant to be sent to the backend. they are purely informational.
        // traderData.removed.forEach((item: BrokerageAccountPositionStaging) => dismissIds.add(item.id));

        setSelectedAcceptHedgeable(acceptHedgeableIds);
        setSelectedAcceptNonHedgeable(acceptNonHedgeableIds);
        setSelectedDismiss(dismissIds);
    }, [diff_data, selectedTraderId]);

    const isLoading = diffResult?.isLoading;
    const isRefreshing = diffResult?.isFetching;
    const encounteredError = !isLoading && (!!diffResult?.error || !diffResult?.data?.success);

    // Get trader options for combobox, including "Unknown" if present
    const traderOptions: SelectOptionItem<string>[] = useMemo(() => {
        const options: SelectOptionItem<string>[] = [];
        
        if (traders_list) {
            options.push(...traders_list.map((trader: { id: string; name: string }) => ({
                value: trader.id,
                label: trader.name
            })));
        }

        // Add "Unknown" if it exists in the response
        if (diff_data?.suggested_report && diff_data.suggested_report[UNKNOWN_TRADER_ID]) {
            options.push({
                value: UNKNOWN_TRADER_ID,
                label: 'Unknown'
            });
        }

        return options.sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    }, [traders_list, diff_data]);

    // Get data for selected trader
    const selectedTraderAccepted = useMemo(() => {
        if (!diff_data?.last_session || !selectedTraderId) return [];
        return diff_data.current_positions_by_trader[selectedTraderId] || [];
    }, [diff_data, selectedTraderId]);

    const selectedTraderStaged = useMemo(() => {
        if (!diff_data?.suggested_report || !selectedTraderId) return null;
        return diff_data.suggested_report[selectedTraderId] || null;
    }, [diff_data, selectedTraderId]);

    // Sort: updated_at (most recent first) → customer_name → commodity_type → crop_year
    const sortPositions = (positions: BrokerageAccountPosition[]) => {
        return [...positions].sort((a, b) => {
            // Sort by customer_name
            const customerCompare = (a.customer_name || '').localeCompare(b.customer_name || '');
            if (customerCompare !== 0) return customerCompare;

            // Sort by commodity_type
            const commodityCompare = (a.commodity_type || '').localeCompare(b.commodity_type || '');
            if (commodityCompare !== 0) return commodityCompare;

            if (a.crop_year && b.crop_year && a.crop_year !== b.crop_year) {
                return b.crop_year - a.crop_year;
            }

            if (a.put_or_call && b.put_or_call && a.put_or_call !== b.put_or_call) {
                return b.put_or_call.localeCompare(a.put_or_call);
            }

            // Sort by updated_at (most recent first)
            const aUpdated = new Date(a.updated_at).getTime();
            const bUpdated = new Date(b.updated_at).getTime();
            if (aUpdated !== bUpdated) return bUpdated - aUpdated;

            return 0;
        });
    };

    // Sort staging positions by their brokerage_account_data
    const sortStagingPositions = (positions: BrokerageAccountPositionStaging[]) => {
        return [...positions].sort((a, b) => {
            const posA = a.brokerage_account_data;
            const posB = b.brokerage_account_data;
            
            // Sort by customer_name
            const customerCompare = (posA.customer_name || '').localeCompare(posB.customer_name || '');
            if (customerCompare !== 0) return customerCompare;

            // Sort by commodity_type
            const commodityCompare = (posA.commodity_type || '').localeCompare(posB.commodity_type || '');
            if (commodityCompare !== 0) return commodityCompare;

            if (posA.crop_year && posB.crop_year && posA.crop_year !== posB.crop_year) {
                return posB.crop_year - posA.crop_year;
            }

            if (posA.put_or_call && posB.put_or_call && posA.put_or_call !== posB.put_or_call) {
                return posB.put_or_call.localeCompare(posA.put_or_call);
            }

            return 0;
        });
    };

    const filteredAccepted = sortPositions(selectedTraderAccepted);

    // Calculate totals across all traders
    const totals = useMemo(() => {
        if (!diff_data?.suggested_report) {
            return { totalAcceptedHedgeable: 0, totalAcceptedNonHedgeable: 0, totalElse: 0, fullTotal: 0 };
        }

        let totalAcceptedHedgeable = 0;
        let totalAcceptedNonHedgeable = 0;
        let totalElse = 0;

        Object.values(diff_data.suggested_report).forEach((traderData: DiffBreakdownResult) => {
            totalAcceptedHedgeable += (traderData.suggested_accept_hedgeable?.length || 0) + 
                                     (traderData.suggested_accept_hedgeable_carryover?.length || 0);
            totalAcceptedNonHedgeable += (traderData.suggested_accept_non_hedgeable?.length || 0) + 
                                        (traderData.suggested_accept_non_hedgeable_carryover?.length || 0);
            totalElse += (traderData.suggested_flagged?.length || 0) + 
                        (traderData.suggested_dismiss?.length || 0) + 
                        (traderData.suggested_non_applicable?.length || 0);
        });

        const fullTotal = totalAcceptedHedgeable + totalAcceptedNonHedgeable + totalElse;

        return { totalAcceptedHedgeable, totalAcceptedNonHedgeable, totalElse, fullTotal };
    }, [diff_data]);

    // Direct selection logic for three-way selection
    const handleSelectHedgeable = (id: string) => {
        // Set to hedgeable
        setSelectedAcceptHedgeable(prev => new Set(prev).add(id));
        // Remove from other sets
        setSelectedAcceptNonHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedDismiss(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const handleSelectNonHedgeable = (id: string) => {
        // Set to non-hedgeable
        setSelectedAcceptNonHedgeable(prev => new Set(prev).add(id));
        // Remove from other sets
        setSelectedAcceptHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedDismiss(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    const handleSelectDismiss = (id: string) => {
        // Set to dismiss
        setSelectedDismiss(prev => new Set(prev).add(id));
        // Remove from other sets
        setSelectedAcceptHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
        setSelectedAcceptNonHedgeable(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
        });
    };

    // Check if all positions for selected trader are marked
    const allPositionsMarked = useMemo(() => {
        if (!selectedTraderId || !selectedTraderStaged) return false;

        const allStagingIds = new Set<string>();
        selectedTraderStaged.suggested_accept_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept_non_hedgeable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept_hedgeable_carryover?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept_non_hedgeable_carryover?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_accept_carryover?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_flagged?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_dismiss?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        selectedTraderStaged.suggested_non_applicable?.forEach((item: BrokerageAccountPositionStaging) => allStagingIds.add(item.id));
        // Note: removed positions are not included in backend submission

        const markedIds = new Set([...selectedAcceptHedgeable, ...selectedAcceptNonHedgeable, ...selectedDismiss]);
        
        // Check that all non-removed positions are marked
        const nonRemovedIds = new Set(allStagingIds);
        selectedTraderStaged.removed?.forEach((item: BrokerageAccountPositionStaging) => nonRemovedIds.delete(item.id));
        
        return Array.from(nonRemovedIds).every(id => markedIds.has(id));
    }, [selectedTraderId, selectedTraderStaged, selectedAcceptHedgeable, selectedAcceptNonHedgeable, selectedDismiss]);

    const handleApplyChanges = async () => {
        if (isApplyingChanges || !selectedTraderId) return;
        
        if (!allPositionsMarked) {
            toast({
                title: "Not all positions marked",
                description: "Please mark all positions as accept or dismiss before submitting.",
                variant: "destructive"
            });
            setIsApplyingChanges(false);
            return;
        }

        setIsApplyingChanges(true);

        // Filter out removed positions from submission
        const removedIds = selectedTraderStaged ? new Set(selectedTraderStaged.removed?.map((item: BrokerageAccountPositionStaging) => item.id) || []) : new Set();
        
        const acceptHedgeableIds = Array.from(selectedAcceptHedgeable).filter(id => !removedIds.has(id));
        const acceptNonHedgeableIds = Array.from(selectedAcceptNonHedgeable).filter(id => !removedIds.has(id));
        const dismissIds = Array.from(selectedDismiss).filter(id => !removedIds.has(id));

        try {
            // Note: selectedTraderId might be UNKNOWN_TRADER_ID which is a string, not UUID
            // We need to handle this case - if it's UNKNOWN_TRADER_ID, we might not be able to submit
            // But the API requires UUID, so we'll need to check this
            if (selectedTraderId === UNKNOWN_TRADER_ID) {
                toast({
                    title: "Cannot submit Unknown trader",
                    description: "Unknown trader positions cannot be submitted.",
                    variant: "destructive"
                });
                setIsApplyingChanges(false);
                return;
            }

            const result = await actionOnStaged({
                trader_id: selectedTraderId,
                provider_type: "RJO",
                trading_order_type: "open_position",
                accepted_hedgeable_row_ids: acceptHedgeableIds,
                accepted_non_hedgeable_row_ids: acceptNonHedgeableIds,
                dismissed_row_ids: dismissIds
            });

            if (!result.success) {
                throw result;
            }

            toast({ title: "Marked successfully" });
            // Clear selections will happen via useEffect when data refetches
        } catch (error) {
            console.error("Failed to apply changes", error);
            toast({
                title: "Failed to apply changes",
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsApplyingChanges(false);
        }
    };

    const getSelectionState = (id: string) => {
        if (selectedAcceptHedgeable.has(id)) return 'hedgeable';
        if (selectedAcceptNonHedgeable.has(id)) return 'non_hedgeable';
        if (selectedDismiss.has(id)) return 'dismiss';
        return null;
    };

    const clearSelections = () => {
        setSelectedAcceptHedgeable(new Set());
        setSelectedAcceptNonHedgeable(new Set());
        setSelectedDismiss(new Set());
    };

    const isSectionCollapsed = (key: string, defaultCollapsed: boolean) => {
        const stored = collapsedSections[key];
        return stored ?? defaultCollapsed;
    };

    const toggleSection = (key: string, defaultCollapsed: boolean) => {
        const current = isSectionCollapsed(key, defaultCollapsed);
        setCollapsedSections(prev => ({ ...prev, [key]: !current }));
    };

    const sections = useMemo(() => {
        if (!selectedTraderStaged) return [];
        return buildSections(selectedTraderStaged, viewMode);
    }, [selectedTraderStaged, viewMode]);

    const acceptHedgeableCount = selectedAcceptHedgeable.size;
    const acceptNonHedgeableCount = selectedAcceptNonHedgeable.size;
    const dismissCount = selectedDismiss.size;

    if (encounteredError) {
        return (
            <div className="flex flex-col gap-4 h-full">
                <p className="text-red-500">Error loading data</p>
                <Button variant="outline" onClick={() => {
                    diffResult.refetch();
                }}>
                    Reload
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 h-full">
            {/* Header with Trader Filter */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="w-full sm:min-w-[300px] flex flex-col md:flex-row gap-2 justify-between">
                    <div className="flex items-center px-2 h-10 min-h-10 max-h-10 bg-muted rounded-md text-xs gap-2 whitespace-nowrap">
<span className="text-muted-foreground">Staged Totals:</span>
                        <span 
                            className="text-green-600 font-semibold" 
                            title="Total Hedgeable Positions"
                        >
                            {totals.totalAcceptedHedgeable}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span 
                            className="text-blue-600 font-semibold" 
                            title="Total Non-Hedgeable Positions"
                        >
                            {totals.totalAcceptedNonHedgeable}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span 
                            className="text-yellow-600 font-semibold" 
                            title="Total Else (Flagged, Dismiss, Non-Applicable)"
                        >
                            {totals.totalElse}
                        </span>
                        <span className="mx-1 opacity-50">|</span>
                        <span 
                            className="text-slate-600 font-semibold" 
                            title="Full Total"
                        >
                            {totals.fullTotal}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground" title="Toggle staged positions view">
                            <button
                                type="button"
                                className={cn("px-3 py-1.5 text-xs font-medium rounded-sm transition-all", viewMode === 'default' ? "bg-background text-foreground shadow-sm" : "hover:text-foreground")}
                                onClick={() => setViewMode('default')}
                            >
                                Default
                            </button>
                            <button
                                type="button"
                                className={cn("px-3 py-1.5 text-xs font-medium rounded-sm transition-all", viewMode === 'alternate' ? "bg-background text-foreground shadow-sm" : "hover:text-foreground")}
                                onClick={() => setViewMode('alternate')}
                            >
                                Alternate
                            </button>
                        </div>
                        <Combobox
                            className="min-w-[150px]"
                            popoverClassName="min-w-[150px]"
                            options={traderOptions}
                            value={selectedTraderId ?? ""}
                            onValueChange={(value) => {
                                clearSelections();
                                setSelectedTraderId(value || null);
                            }}
                            placeholder="Filter by trader"
                        />
                    </div>
                </div>
            </div>

            {/* Action Bar */}
            {selectedTraderId && (
                <div className="sticky bottom-0 bg-background border-t p-4 shadow-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-green-600 font-medium">{acceptHedgeableCount} hedgeable</span>
                            <span className="text-blue-600 font-medium">{acceptNonHedgeableCount} non-hedgeable</span>
                            <span className="text-red-600 font-medium">{dismissCount} dismissed</span>
                            {!allPositionsMarked && (
                                <span className="text-yellow-600 font-medium">Not all positions marked</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button 
                                onClick={handleApplyChanges} 
                                disabled={!allPositionsMarked || selectedTraderId === UNKNOWN_TRADER_ID || isApplyingChanges}
                            >
                                {isApplyingChanges ? "Applying..." : "Apply Changes"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content - 50/50 Split on Desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
                {/* Current Open Positions */}
                <Card className="flex flex-col">
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            <span>Current Open Positions ({filteredAccepted.length})</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-y-auto flex flex-col gap-2 h-[70vh]">
                        {isLoading ? (
                            <div className="space-y-2">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-24" />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {filteredAccepted.map(position => (
                                    <PositionCard key={position.id} position={position} />
                                ))}
                                {filteredAccepted.length === 0 && (
                                    <div className="text-center text-muted-foreground py-8">
                                        No open positions found
                                    </div>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Staged Positions */}
                <Card className="flex flex-col">
                    <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                            <span>Staged Positions</span>
                            {isRefreshing && <Loader className="h-4 w-4 animate-spin" />}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="overflow-y-auto flex flex-col gap-2 h-[70vh]">
                        {isLoading ? (
                            <div className="space-y-2">
                                {[...Array(5)].map((_, i) => (
                                    <Skeleton key={i} className="h-24" />
                                ))}
                            </div>
                        ) : !selectedTraderStaged ? (
                            <div className="text-center text-muted-foreground py-8">
                                {selectedTraderId ? "No staged positions for this trader" : "Select a trader to view staged positions"}
                            </div>
                        ) : (
                            <>
                                {sections.map(section => (
                                    <StagedSection
                                        key={section.key}
                                        section={section}
                                        collapsed={isSectionCollapsed(section.key, section.defaultCollapsed ?? false)}
                                        onToggle={() => toggleSection(section.key, section.defaultCollapsed ?? false)}
                                        sortStagingPositions={sortStagingPositions}
                                        getSelectionState={getSelectionState}
                                        onSelectHedgeable={handleSelectHedgeable}
                                        onSelectNonHedgeable={handleSelectNonHedgeable}
                                        onSelectDismiss={handleSelectDismiss}
                                    />
                                ))}

                                {sections.every(section => section.data.length === 0) && (
                                    <div className="text-xs text-muted-foreground py-2 text-center">No staged positions</div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
